import { useReducer, useEffect, useRef, useState, useMemo } from 'react';
import useFlags from '@/hooks/useFlags';
import SpeechStream, { SupportedSourceLanguage, voiceForSpeed } from './SpeechStream';
import playbackStateReducer from './playbackStateReducer';

const ADMIN_CONFIG = 'TCIadminR1';
const USER_CONFIG = 'TCIR1';
const LEGACY_CONFIG = 'TCIv351R1';

const SPEECHSTREAM_VERSION = '3.11.0';

export const scriptSrc = () => (
  `https://toolbar.speechstream.net/SpeechStream/${SPEECHSTREAM_VERSION}/speechstream.js`
);

const speechstreamConfig = (
  canUseUpdatedSpeechstream?: boolean,
  showSpeechstreamToolbar?: boolean,
) => {
  if (canUseUpdatedSpeechstream) {
    return ({
      speechstreamConfig: showSpeechstreamToolbar ? ADMIN_CONFIG : USER_CONFIG,
    });
  }

  return ({
    speechstreamConfig: LEGACY_CONFIG,
  });
};

interface SpeechStreamHook {
  canUseUpdatedSpeechstream: boolean;
  loaded: boolean;
  playing: boolean;
  selectedTranslationLanguage: () => string | null;
  setNoScroll: (noScroll: boolean) => void;
  setTranslationDestination: (language: string) => Promise<{ englishDescription: string }>;
  setTranslationSource: (language: SupportedSourceLanguage) => SetSpeechstreamSourceLanguage;
  setVoice: (voice: string) => void;
  stop: () => void;
  toggleSpeech: () => void;
  translateSelection: () => void;
  translationLanguages: () => string[];
  translationLanguagesMap: () => Record<string, boolean>;
}

interface Params {
  page?: number;
  locale: Locale;
  idToRead: string;
  speed?: string;
}

const useSpeechStream = ({ page, locale, idToRead, speed }: Params): SpeechStreamHook => {
  const { canUseUpdatedSpeechstream, showSpeechstreamToolbar } = useFlags();
  const config = speechstreamConfig(canUseUpdatedSpeechstream, showSpeechstreamToolbar);
  const src = scriptSrc();
  const [loaded, setIsLoaded] = useState(!!window.speechstreamToolbarLoaded);
  const initialState = { paused: false, playing: false };
  const [{ playing, paused }, playbackStateDispatch] = useReducer(playbackStateReducer, initialState);
  const shouldPause = useMemo(() => playing || paused, [playing, paused]);

  // Spanish reading always uses Paulina, and the legacy config doesn't have the Joey/Matthew voices.
  const speedVoice = (canUseUpdatedSpeechstream && speed && locale?.toLowerCase() !== 'es')
    ? voiceForSpeed(speed)
    : null;
  const voiceRef = useRef(speedVoice);
  voiceRef.current = speedVoice;

  const speechstreamParams = { idToRead, playbackStateDispatch, voice: speedVoice };

  const playOrPause = () => {
    if (shouldPause) {
      SpeechStream.pause(playbackStateDispatch);
    } else {
      SpeechStream.play(speechstreamParams);
    }
  };

  const playElement = () => {
    if (!window.lastIdSpoken) return SpeechStream.play(speechstreamParams);
    if (idToRead === window.lastIdSpoken) return playOrPause();

    SpeechStream.stop();
    SpeechStream.play(speechstreamParams);
  };

  const toggleSpeech = () => {
    if (!loaded) return null;

    return idToRead ? playElement() : playOrPause();
  };

  const translateSelection = () => {
    if (!loaded) return null;

    return SpeechStream.translate();
  };

  const translationLanguages = () => {
    if (!loaded) return [];

    return SpeechStream.translationLanguages();
  };

  const translationLanguagesMap = () => {
    if (!loaded) return {};

    return SpeechStream.translationLanguagesMap();
  };

  const selectedTranslationLanguage = () => {
    if (!loaded) return;

    return SpeechStream.selectedDestinationLanguage();
  };

  const setTranslationDestination = (language: string) => (
    SpeechStream.setTranslationDestination(language)
  );

  const setTranslationSource = (language: SupportedSourceLanguage) => {
    if (loaded) return SpeechStream.setTranslationSource(language);
  };

  const setVoice = (voice: string) => { if (loaded) SpeechStream.setVoice(voice); };

  const setNoScroll = (noScroll: boolean) => {
    if (
      loaded
      && window.speechstream
      && window.speechstream.domControlTools
      && typeof window.speechstream.domControlTools.setNoScroll === 'function'
    ) SpeechStream.setNoScroll(noScroll);
  };

  const stop = () => {
    if (!loaded) return null;

    return SpeechStream.stop(playbackStateDispatch);
  };

  // AIDEV-NOTE: SpeechStream installs itself as a global singleton on `window`, so its script
  // must load exactly once per page. The shared `useScript` helper removes the <script> tag on
  // unmount, which on slide navigation in present mode caused the next slide to re-add the tag
  // and re-init the library on top of the still-playing instance — producing overlapping audio.
  // Load directly here and never clean up.
  useEffect(() => {
    if (document.querySelector(`script[src="${src}"]`)) return;

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    Object.keys(config).forEach((key) => { script.dataset[key] = config[key]; });
    document.body.appendChild(script);
  }, []);

  useEffect(() => SpeechStream.addEventListener({
    locale,
    page,
    setIsLoaded,
    showSpeechstreamToolbar,
    voiceRef,
  }), []);

  useEffect(() => {
    if (speedVoice) setVoice(speedVoice);
  }, [loaded, speedVoice]);

  // AIDEV-NOTE: Even with the singleton script load, audio playback survives this component
  // unmounting (e.g. slide navigation). Stop on unmount when *this* hook instance was the one
  // playing, so audio from the previous slide doesn't bleed into the next.
  const activeRef = useRef(false);
  activeRef.current = playing || paused;
  useEffect(() => () => {
    if (activeRef.current && window.speechstream?.speechTools) {
      window.speechstream.speechTools.stop();
    }
  }, []);

  return {
    canUseUpdatedSpeechstream,
    loaded,
    playing,
    selectedTranslationLanguage,
    setNoScroll,
    setTranslationDestination,
    setTranslationSource,
    setVoice,
    stop,
    toggleSpeech,
    translateSelection,
    translationLanguages,
    translationLanguagesMap,
  };
};

export default useSpeechStream;
